import { FC, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAccess } from "../../hooks/useAccess";
import { icons } from "../../icons/IconSet";
import RotateIcon from "../common/RotateIcon";
import { INavItem } from "./INav";
import SideBarNavLink from "./SideBarNavLink";

const SideBarNav: FC = () => {
  const { getPageAccess } = useAccess();
  const { pathname } = useLocation();

  const filterNav = (nav: INavItem[]): INavItem[] =>
    nav.reduce((acc, item) => {
      if (item?.isNotAvailable || !item?.name) return acc;
      if (!item?.children?.length) return [...acc, item];

      const children = filterNav(item.children);
      if (children?.length) return [
        ...acc,
        {
          ...item,
          children
        }
      ];
      return acc;
    }, [] as INavItem[]);

  const nav: INavItem[] = filterNav([
    { name: 'Orders Service', children: [
      { name: 'Recent Orders', to: '/orders', isNotAvailable: !getPageAccess('/orders') },
      { name: 'Orders By Date', to: '/orders-by-date', isNotAvailable: !getPageAccess('/orders-by-date') },
      { name: 'Orders By Vendor', to: '/orders-by-vendor', isNotAvailable: !getPageAccess('/orders-by-vendor') },
      { name: 'Orders By Vehicle', to: '/orders-by-vehicle', isNotAvailable: !getPageAccess('/orders-by-vehicle') },
      { name: 'Orders By Part Type', to: '/orders-by-part-type', isNotAvailable: !getPageAccess('/orders-by-part-type') },
      { name: 'Orders By Status', to: '/orders-by-status', isNotAvailable: !getPageAccess('/orders-by-status') },
      { name: 'Orders By Website', to: '/orders-by-website', isNotAvailable: !getPageAccess('/orders-by-website') },
      { name: 'Orders By State', to: '/orders-by-state', isNotAvailable: !getPageAccess('/orders-by-state') },
      { name: 'Orders By DMA', to: '/orders-by-dma', isNotAvailable: !getPageAccess('/orders-by-dma') },
      { name: 'Orders By Payment Type', to: '/orders-by-payment-type', isNotAvailable: !getPageAccess('/orders-by-payment-type') },
    ]},
  ]);

  const [openedNavsIndxs, setOpenedNavsIndxs] = useState<Record<string, boolean>>(nav.reduce((acc, { children }, i) => ({
    ...acc,
    [i]: !!children?.length
  }), {} as Record<string, boolean>));

  return (
    <nav className="flex flex-1 flex-col gap-4">
      {nav.map(({ name, children, to, onClick }, i) => children?.length
        ? (
          <div key={`${name}-${i}`} className=" overflow-hidden">
            <div
              className="text-text_pale px-2 flex justify-between cursor-pointer"
              tabIndex={-1}
              role="button"
              onClick={() => setOpenedNavsIndxs((prevState) => ({
                ...prevState,
                [i]: !prevState?.[i],
              }))}
            >
              {name}
              <RotateIcon isShown={openedNavsIndxs?.[i]}>
                {icons('chevron-down', { width: '16px' })}
              </RotateIcon>
            </div>
            <div className={`transition-all duration-300
              ${openedNavsIndxs?.[i]
                ? 'opacity-100 translate-y-1 h-auto'
                : 'opacity-0 -translate-y-0 h-0'}
            `}>
              {children.map((child, j) => !child?.to || child?.disabled
                ? <div
                    key={`${child?.name}-${j}`}
                    className="block py-[6px] px-4 opacity-50"
                  >
                    {child?.name}
                  </div>
                : (
                  <SideBarNavLink
                    key={`${child?.to}-${child?.name}`}
                    to={child?.to}
                    isCurrent={child.isCurrent || pathname === child?.to}
                  >
                    {child?.name || '-'}
                  </SideBarNavLink>
              ))}
            </div>
          </div>
        ) : (
          !to
            ? <div className="">{name}</div>
            : (
              <SideBarNavLink key={`${to}-${name}`} to={to}>
                {name || '-'}
              </SideBarNavLink>
            )
        )
      )}
    </nav>
  );
}

export default SideBarNav;
